
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute } from 'vue-router'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import RadioGroup from '@/common/components/UI/RadioGroup/index.vue'

// @module components
import AddSubdirectionModal from '@/modules/TMSModule/components/AddSubdirectionModal/index.vue'
import DeleteSubdirectionModal from '@/modules/TMSModule/components/DeleteSubdirectionModal/index.vue'
import DirectionsTMSActions from '@/modules/TMSModule/components/DirectionsTMSActions/index.vue'
import DirectionsImportInternal from '@/modules/TMSModule/components/DirectionsImportInternal/index.vue'

// @types
import {
    DeleteSubdirectionPayload,
    DirectionDetailedTMS,
    DirectionSla,
    DirectionTMS,
    InternalDirection,
    UpdateDirectionTMSPayload,
} from '@/new-services/types/tms'

// @services
import tmsAPI from '@/new-services/tms.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

import statusIcon from '@/assets/icons/status.svg'
import logistIcon from '@/assets/icons/logist.svg'
import oneCIcon from '@/assets/icons/oneC.svg'
import zoneIcon from '@/assets/icons/zone.svg'
import cityFromIcon from '@/assets/icons/cityFrom.svg'
import cityToIcon from '@/assets/icons/cityTo.svg'
import deattachIcon from '@/assets/icons/deattach.svg'
import importIcon from '@/assets/icons/import.svg'
import parentIcon from '@/assets/icons/parent.svg'
import deattachRedIcon from '@/assets/icons/deattachRed.svg'

import Deliveries from '../NewDispatcher/Deliveries.vue'
import useActions from '@/common/composable/useActions'

const SLA_TABS = [
    { id: 1, name: 'LTL' },
    { id: 2, name: 'FTL' },
]
const COST_TABS = [
    { id: 1, name: 'LTL' },
    { id: 2, name: 'FTL' },
]

export default defineComponent({
    components: {
        PageTitle,
        Loader,
        NewButton,
        AddSubdirectionModal,
        DeleteSubdirectionModal,
        statusIcon,
        logistIcon,
        oneCIcon,
        zoneIcon,
        cityFromIcon,
        cityToIcon,
        deattachIcon,
        importIcon,
        Deliveries,
        RadioGroup,
        parentIcon,
        deattachRedIcon,
        DirectionsImportInternal,
        'directions-tms-actions': DirectionsTMSActions,
    },
    setup() {
        const notifications = useNotifications()
        const route = useRoute()
        const directionUrlId = Number(route.params.id as string)
        const { havePermission } = useAccount()

        const { actions_state, startUpdate } = useActions<DirectionTMS>()

        const state = reactive({
            is_loading: false,
            current_tab_sla: 1,
            current_tab_cost: 1,
            is_failed: false,
            is_add_direction: false,
            is_sla: true,
            is_excel: false,
            //
            direction: null as DirectionDetailedTMS | null,
            sla_ltl: null as DirectionSla[] | null,
            sla_ftl: null as DirectionSla[] | null,
        })

        const delete_state = reactive({
            directionId: null as number | null,
            parentDirectionId: null as number | null,

            child: null as string | null,
            parent: null as string | null,

            is_open: false,
            is_child: false,
        })

        //

        const loadDirection = async () => {
            try {
                state.is_loading = true

                const response = await tmsAPI.getDirection(directionUrlId)
                state.direction = response.data.data

                if (state.direction.fromCity && state.direction.whereCity) {
                    const responseSlaLtl = await tmsAPI.getDirectionSlaLtl(
                        state.direction.fromCity.id,
                        state.direction.whereCity.id
                    )
                    const responseSlaFtl = await tmsAPI.getDirectionSlaFtl(
                        state.direction.fromCity.id,
                        state.direction.whereCity.id
                    )

                    state.sla_ltl = responseSlaLtl.data.data
                    state.sla_ftl = responseSlaFtl.data.data

                    if (!state.sla_ltl.length && !state.sla_ftl.length) {
                        state.is_sla = false
                    }
                }

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении детальной информации произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const toggleAddSubdirection = () => (state.is_add_direction = !state.is_add_direction)

        const resetDeleteState = () => {
            delete_state.directionId = null
            delete_state.parentDirectionId = null
            delete_state.is_open = false
        }

        const deleteSubdirection = async () => {
            if (!delete_state.directionId || !delete_state.parentDirectionId) return

            try {
                state.is_loading = true

                const payload = {
                    directionId: delete_state.directionId,
                    parentDirectionId: delete_state.parentDirectionId,
                }
                await tmsAPI.deleteSubdirection(payload)
                loadDirection()
                notifications.notify({
                    title: 'Направление успешно откреплено!',
                    type: 'success',
                })
                resetDeleteState()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При удалении произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const toggleDeleteSubdirection = (
            directionId: number,
            child: string,
            parent: string,
            type?: string
        ) => {
            delete_state.child = child
            delete_state.parent = parent
            delete_state.is_open = !delete_state.is_open

            if (delete_state.is_open && type === 'child') {
                delete_state.is_child = true

                delete_state.directionId = directionUrlId
                delete_state.parentDirectionId = directionId
            } else {
                delete_state.is_child = false

                delete_state.directionId = directionId
                delete_state.parentDirectionId = directionUrlId
            }
        }

        const addSubdirection = async (fromCityId: number, toCityId: number) => {
            if (!state.direction) return

            try {
                state.is_loading = true

                const payload = {
                    fromCityId,
                    toCityId,
                    parentDirectionId: state.direction.id,
                }

                await tmsAPI.addSubdirection(payload)
                toggleAddSubdirection()
                loadDirection()
                notifications.notify({ title: 'Внутреннее направление создано!', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При создании произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        //

        const pageTitle = computed(() => {
            if (state.direction) {
                const { fromCity, whereCity } = state.direction

                return `${fromCity.name} - ${whereCity.name}`
            }

            return '... - ...'
        })

        const dirName = (dir: InternalDirection) => {
            if (!dir.fromCity || !dir.whereCity) {
                return '-'
            }

            return `${dir.fromCity.name} - ${dir.whereCity.name}`
        }

        const isLtlSla = computed(() => state.current_tab_sla === 1)
        const isFtlSla = computed(() => state.current_tab_sla === 2)

        const isParent = computed(() => {
            return Boolean(
                state.direction &&
                    state.direction.directionType &&
                    state.direction.directionType.id === 1
            )
        })
        const isInternal = computed(() =>
            Boolean(
                state.direction &&
                    state.direction.directionType &&
                    state.direction.directionType.id === 2
            )
        )

        const getSla = (type: string, period: number, tariff?: string) => {
            if (type === 'ltl') {
                if (!state.sla_ltl) return
                if (tariff) {
                    return state.sla_ltl.find(
                        (item) => item.periodId === period && item.tariff === tariff
                    )
                }
                return state.sla_ltl.find((item) => item.periodId === period)
            } else {
                if (!state.sla_ftl) return
                return state.sla_ftl.find((item) => item.periodId === period)
            }
        }

        const updateDirection = async (payload: UpdateDirectionTMSPayload) => {
            if (!actions_state.initial_value) return
            const { id } = actions_state.initial_value

            try {
                actions_state.is_actions_loading = true

                await tmsAPI.updateDirectionTMS(id, payload)

                await loadDirection()

                actions_state.is_actions_opened = false
                notifications.notify({
                    title: 'Направление успешно отредактировано!',
                    type: 'success',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Ошибка при редактировании направления`,
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const toggleExcel = () => {
            state.is_excel = !state.is_excel
        }

        //

        onBeforeMount(loadDirection)

        //

        return {
            state,
            delete_state,
            actions_state,
            //
            loadDirection,
            dirName,
            toggleAddSubdirection,
            addSubdirection,
            deleteSubdirection,
            toggleDeleteSubdirection,
            havePermission,
            getSla,
            resetDeleteState,
            updateDirection,
            startUpdate,
            toggleExcel,
            //
            pageTitle,
            directionUrlId,
            isParent,
            isLtlSla,
            isFtlSla,
            isInternal,
            //
            SLA_TABS,
            COST_TABS,
        }
    },
})
